import React, { useState } from 'react';
import styled from 'styled-components';
import { Avatar } from '@boringer-avatars/react';
import { useActiveClassData } from '../state/hooks';
import { ClassChangeRequestDialog } from './ClassChangeRequestDialog';
import Loader from '../Loader';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  height: ${(props) => (props.$fillHeight ? '100%' : 'auto')};
`;

const InfoSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const InfoText = styled.p`
  margin: 0;
  font-family: var(--font-family-ibm_plex_sans);
  font-size: 20px;
  font-weight: 600;
  line-height: 28px;
  color: #2d2b2e;
`;

const InfoSubText = styled.p`
  margin: 0;
  font-family: var(--font-family-ibm_plex_sans);
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  color: #55505c;
`;

const ClassSection = styled.div`
  padding: 0;
`;

const StudentsSection = styled.div`
  padding: 16px;
  background: #fbfbfb;
  border: 1px solid #c9c6cc80;
  border-radius: 8px;
`;

const SectionTitle = styled.h3`
  margin: 0 0 12px;
  font-family: var(--font-family-ibm_plex_sans);
  font-size: 22px;
  font-weight: 600;
  color: #2d2b2e;
`;

const ClassContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const ClassName = styled.button`
  border: 1px solid ${(props) => (props.$isSelected ? '#7000db' : '#c9c6cc')};
  border-radius: 999px;
  background: ${(props) => (props.$isSelected ? '#f1e6fc' : '#fff')};
  color: ${(props) => (props.$isSelected ? '#7000db' : '#2d2b2e')};
  padding: 8px 16px;
  font-family: var(--font-family-ibm_plex_sans);
  font-size: ${(props) => props.$classFontSize || '20px'};
  cursor: pointer;
`;

const StudentGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const Student = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border: 1px solid #d9d6dd;
  border-radius: 8px;
  background: #fff;
`;

const StudentName = styled.span`
  font-family: var(--font-family-ibm_plex_sans);
  font-size: 14px;
  color: #2d2b2e;
  white-space: nowrap;
`;

const Footer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: ${(props) => (props.$pinBottom ? 'auto' : '0')};
`;

const RequestButton = styled.button`
  border: 1px solid #2e2177;
  border-radius: 999px;
  background: #fff;
  color: #2e2177;
  padding: 10px 18px;
  font-family: var(--font-family-ibm_plex_sans);
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
`;

export function ClassListContent({
  requestButton = null,
  classFontSize = '20px',
  pinRequestButtonBottom = false,
  classesData = null,
}) {
  const [selectedClassIndex, setSelectedClassIndex] = useState(0);
  const [classChangeRequestOpen, setClassChangeRequestOpen] = useState(false);
  const { data: classData, isLoadingdata: isLoadingClassData } = useActiveClassData();

  if (!classesData && isLoadingClassData) {
    return <Loader />;
  }

  const classes = classesData || classData || [];
  const selectedClass = classes[selectedClassIndex] || classes[0];

  return (
    <Container $fillHeight={pinRequestButtonBottom}>
      <InfoSection>
        <InfoText>These are your classes and students with JeddAI accounts.</InfoText>
        <InfoSubText>
          You can request a class change any time if there are class movements or students are missing.
        </InfoSubText>
      </InfoSection>

      <ClassSection>
        <SectionTitle>Classes</SectionTitle>
        <ClassContainer>
          {classes.map((classItem, index) => (
            <ClassName
              key={`${classItem.id || 'class'}-${index}`}
              type="button"
              onClick={() => setSelectedClassIndex(index)}
              $isSelected={selectedClassIndex === index}
              $classFontSize={classFontSize}
            >
              {classItem.title}
            </ClassName>
          ))}
        </ClassContainer>
      </ClassSection>

      <StudentsSection>
        <SectionTitle>Students</SectionTitle>
        <StudentGrid>
          {(selectedClass?.students || []).map((student, index) => (
            <Student key={student.id || index}>
              <Avatar title={false} size={20} variant="beam" name={student?.name} square={false} />
              <StudentName>{student?.name}</StudentName>
            </Student>
          ))}
        </StudentGrid>
      </StudentsSection>

      <Footer $pinBottom={pinRequestButtonBottom}>
        {requestButton || (
          <RequestButton
            type="button"
            onClick={() => setClassChangeRequestOpen(true)}
          >
            Request class change
          </RequestButton>
        )}
      </Footer>
      <ClassChangeRequestDialog
        open={classChangeRequestOpen}
        onClose={() => setClassChangeRequestOpen(false)}
      />
    </Container>
  );
}
